// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * NotarialService - Notariële wensen & nalatenschap
 * Stores all notarial data as a single encrypted blob in db.wishes
 * using moduleKey 'notarial'.
 */
import { wizardLoad, wizardSave } from '@/services/wizard'
import { saveModuleProgress }     from '@/services/profile'

// ── Data types ────────────────────────────────────────────────────────────────

export interface ExecuteurData {
  name:        string
  relation:    string
  phone:       string
  dob?:        string   // V49-UAT: geboortedatum executeur (optioneel, backward compat)
  // Tweede
  reserveName:     string
  reserveRelation: string
  reservePhone:    string
  reserveDob?:     string
}

export interface VoogdijData {
  name:            string
  relation:        string   // keuzelijst-waarde
  relationOther:   string   // vrije tekst als relation === 'Anders'
  phone:           string
  notes:           string
  dob?:            string   // V49-UAT: geboortedatum voogd (optioneel, backward compat)
  // Tweede voogd (V42)
  reserveName:          string
  reserveRelation:      string
  reserveRelationOther: string
  reservePhone:         string
  reserveDob?:          string
}

export interface BezittingEntry {
  bezitting: string   // wat
  persoon:   string   // aan wie
  opmerking: string
}

export interface DigitaalEntry {
  platform: string
  actie:    string
}

export interface GoedDoelEntry {
  organisatie: string
  bedragOfPct: string
  opmerking:   string
}

export interface NotarialData {
  // 1. Executeur
  executeur:           ExecuteurData
  // 2. Voogdij
  voogdij:             VoogdijData
  // 3. Erfgenamen vrije tekst
  erfgenaanWensen:     string
  // 4. Aan wie geef ik wat
  bezittingen:         BezittingEntry[]
  // 5. Digitale nalatenschap (accounts)
  digitaleAccounts:    DigitaalEntry[]
  // 6. Goede doelen
  goedeDoelenLijst:    GoedDoelEntry[]
  // 7. Onderneming
  ondernemingWens:     string
  ondernemingAfwikkel: string
  ondernemingBoekhouder:string
  ondernemingOverig:   string
  ondernemingWebsite:  string
  ondernemingEmail:    string
  ondernemingTelefoon: string
  ondernemingBank:     string
  ondernemingVestigingen: string
  // 8. Buitenlandse bezittingen
  buitenlandseBezittingen: Array<{ land: string; omschrijving: string; bijzonderheden: string }>
  // 9. Persoonlijke boodschap
  persoonlijkeBoodschap: string
  // V41: Huwelijkse voorwaarden
  huwelijkseVoorwaarden:        string   // 'yes' | 'no' | 'unknown' | ''
  huwelijkseVoorwaardenNotaris: string
  huwelijkseVoorwaardenDatum:   string
  huwelijkseVoorwaardenLocatie: string
  // V41: KvK onderneming
  kvkNummer:          string
  kvkVestigingsadres: string
  kvkOpvolger:        string
  // Fase 2: aanvullende onderneming/KVK-velden (reconstructie V46)
  bedrijfsnaam:       string
  handelsnaam:        string
  btwNummer:          string
  rechtsvorm:         string
  kvkCheckDatum:      string
  kvkCheckStatus:     string
}

export const EMPTY_EXECUTEUR: ExecuteurData = {
  name: '', relation: '', phone: '',
  reserveName: '', reserveRelation: '', reservePhone: '',
}

export const EMPTY_VOOGDIJ: VoogdijData = {
  name: '', relation: '', relationOther: '', phone: '', notes: '',
  reserveName: '', reserveRelation: '', reserveRelationOther: '', reservePhone: '',
}

export const EMPTY_NOTARIAL: NotarialData = {
  executeur:             EMPTY_EXECUTEUR,
  voogdij:               EMPTY_VOOGDIJ,
  erfgenaanWensen:       '',
  bezittingen:           [],
  digitaleAccounts:      [],
  goedeDoelenLijst:      [],
  ondernemingWens:       '',
  ondernemingAfwikkel:   '',
  ondernemingBoekhouder: '',
  ondernemingOverig:     '',
  ondernemingWebsite:    '',
  ondernemingEmail:      '',
  ondernemingTelefoon:   '',
  ondernemingBank:       '',
  ondernemingVestigingen: '',
  buitenlandseBezittingen: [],
  persoonlijkeBoodschap:   '',
  huwelijkseVoorwaarden:        '',
  huwelijkseVoorwaardenNotaris: '',
  huwelijkseVoorwaardenDatum:   '',
  huwelijkseVoorwaardenLocatie: '',
  kvkNummer:          '',
  kvkVestigingsadres: '',
  kvkOpvolger:        '',
  bedrijfsnaam:       '',
  handelsnaam:        '',
  btwNummer:          '',
  rechtsvorm:         '',
  kvkCheckDatum:      '',
  kvkCheckStatus:     '',
}

// ── CRUD ──────────────────────────────────────────────────────────────────────

export async function loadNotarial(userId: string): Promise<NotarialData | null> {
  return wizardLoad<NotarialData>(userId, 'notarial')
}

export async function saveNotarial(userId: string, data: NotarialData): Promise<void> {
  // Calculate completion
  const filled = [
    data.executeur.name,
    data.voogdij.name,
    data.erfgenaanWensen,
    data.persoonlijkeBoodschap,
  ].filter(Boolean).length
  +  (data.bezittingen.length > 0 ? 1 : 0)
  +  (data.digitaleAccounts.length > 0 ? 1 : 0)
  +  (data.goedeDoelenLijst.length > 0 ? 1 : 0)

  await wizardSave(userId, 'notarial', data, filled)
  await saveModuleProgress(userId, 'notarial', filled, 7)
}

// ── Completion helpers ────────────────────────────────────────────────────────

export function calcNotarialProgress(data: NotarialData): { pct: number; count: number; total: number } {
  const checks = [
    !!data.executeur.name,
    !!data.voogdij.name || true,            // optional - voogdij only relevant with minor children
    !!data.erfgenaanWensen,
    data.bezittingen.length > 0,
    data.digitaleAccounts.length > 0,
    data.goedeDoelenLijst.length > 0,
    !!data.persoonlijkeBoodschap,
  ]
  const count = checks.filter(Boolean).length
  const total = checks.length
  return { pct: Math.round((count / total) * 100), count, total }
}
